/**
 * Canvas-based signature pad.
 * Manages its own canvas element; designed to be embedded inside a view component.
 *
 * Emits native DOM events on the canvas element:
 *   'sig:signed'  — user has drawn something
 *   'sig:cleared' — canvas was cleared
 */
export class SignaturePad {
  #drawing = false;
  #hasSigned = false;
  #canvas;
  #ctx;
  #cleanups = [];
  #opts;

  /**
   * @param {HTMLCanvasElement} canvas
   * @param {{ color?: string, lineWidth?: number }} opts
   */
  constructor(canvas, opts = {}) {
    this.#canvas = canvas;
    this.#opts = { color: 'var(--surface-navy, #0d2240)', lineWidth: 2.5, ...opts };
    this.#init();
    this.#bind();
  }

  #init() {
    const rect = this.#canvas.getBoundingClientRect();
    this.#canvas.width  = Math.max(Math.round(rect.width * window.devicePixelRatio), 1);
    this.#canvas.height = 112;
    this.#ctx = this.#canvas.getContext('2d');
    this.#ctx.strokeStyle = this.#opts.color;
    this.#ctx.lineWidth   = this.#opts.lineWidth;
    this.#ctx.lineCap     = 'round';
    this.#ctx.lineJoin    = 'round';
  }

  #pos(e) {
    const r  = this.#canvas.getBoundingClientRect();
    const sx = this.#canvas.width  / r.width;
    const sy = this.#canvas.height / r.height;
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * sx, y: (src.clientY - r.top) * sy };
  }

  #bind() {
    const onDown = e => {
      e.preventDefault();
      const p = this.#pos(e);
      this.#drawing = true;
      this.#ctx.beginPath();
      this.#ctx.moveTo(p.x, p.y);
    };
    const onMove = e => {
      e.preventDefault();
      if (!this.#drawing) return;
      const p = this.#pos(e);
      this.#ctx.lineTo(p.x, p.y);
      this.#ctx.stroke();
      if (!this.#hasSigned) {
        this.#hasSigned = true;
        this.#canvas.dispatchEvent(new CustomEvent('sig:signed', { bubbles: true }));
      }
    };
    const onUp = e => { e.preventDefault(); this.#drawing = false; };

    const opts = { passive: false };
    this.#canvas.addEventListener('mousedown',  onDown);
    this.#canvas.addEventListener('mousemove',  onMove);
    this.#canvas.addEventListener('mouseup',    onUp);
    this.#canvas.addEventListener('touchstart', onDown, opts);
    this.#canvas.addEventListener('touchmove',  onMove, opts);
    this.#canvas.addEventListener('touchend',   onUp,   opts);

    this.#cleanups.push(() => {
      this.#canvas.removeEventListener('mousedown',  onDown);
      this.#canvas.removeEventListener('mousemove',  onMove);
      this.#canvas.removeEventListener('mouseup',    onUp);
      this.#canvas.removeEventListener('touchstart', onDown);
      this.#canvas.removeEventListener('touchmove',  onMove);
      this.#canvas.removeEventListener('touchend',   onUp);
    });
  }

  restore(dataUrl) {
    if (!dataUrl) return;
    const img = new Image();
    img.onload = () => {
      this.#ctx.drawImage(img, 0, 0, this.#canvas.width, this.#canvas.height);
      this.#hasSigned = true;
      this.#canvas.dispatchEvent(new CustomEvent('sig:signed', { bubbles: true }));
    };
    img.src = dataUrl;
  }

  clear() {
    this.#ctx.clearRect(0, 0, this.#canvas.width, this.#canvas.height);
    this.#hasSigned = false;
    this.#canvas.dispatchEvent(new CustomEvent('sig:cleared', { bubbles: true }));
  }

  get hasSigned()  { return this.#hasSigned; }
  get dataURL()    { return this.#hasSigned ? this.#canvas.toDataURL('image/png') : ''; }

  destroy() {
    this.#cleanups.forEach(fn => fn());
    this.#cleanups = [];
  }
}
